import { TRACK_LOAD_BATCH } from '@disco/constants/ActionTypes'
import { pushUINotification } from '@disco/utils/notifications'
import NavigationService from '@disco/state-management/NavigationService'
import type { GetState } from 'types/redux'
import type { LyricsMetadata, Track, TrackId } from 'types/track'

/**
 * Handles the completion of lyrics transcription for a track.
 * Updates the track with lyrics data and shows a success notification.
 * @param trackId - The ID of the track that was transcribed
 * @param trackName - The name of the track for notification purposes
 * @param lyrics - The transcribed lyrics text
 * @param metadata - Additional metadata about the transcription
 * @returns Thunk that updates the track and shows completion notification
 */
export const handleLyricsComplete =
  (
    trackId: TrackId,
    trackName: string,
    lyrics?: string,
    metadata?: LyricsMetadata,
  ): ((dispatch: any, getState: GetState) => any) =>
  (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    // Update track in redux if exists
    const track = getState().tracks[trackId]

    if (track) {
      const updatedTrack: Track = {
        ...track,
        metadata: { ...track.metadata, Lyrics: lyrics },
        metadata_extras: {
          ...track.metadata_extras,
          Lyrics: {
            confidence: metadata?.confidence,
            source: metadata?.source,
          },
        },
      }
      dispatch({
        type: TRACK_LOAD_BATCH,
        list: [updatedTrack],
      })
    }

    // Show toast notification and kill pending notification if exists
    pushUINotification({
      _replaceNotificationWithUID: `lyric-transcription-pending-${trackId}`,
      uid: `lyric-transcription-result-${trackId}`,
      level: 'success',
      title: 'Transcription complete!',
      message: `Lyric transcription has finished for ‘${trackName}’.`,
      action: {
        label: 'View lyrics',
        callback: () =>
          NavigationService.push(
            `/explorer/tracks/${trackId}/edit-metadata?tab=1`,
          ),
      },
      dismissible: 'button',
    })
  }

/**
 * Handles the start of lyrics transcription for a track.
 * Updates the track to indicate transcription is in progress.
 * @param trackId - The ID of the track being transcribed
 * @returns Thunk that updates the track with transcription status
 */
export const handleLyricsStarting =
  (trackId: TrackId): ((dispatch: any, getState: GetState) => any) =>
  (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    // Update track in redux if exists
    const track = getState().tracks[trackId]

    if (track) {
      const { Lyrics: _, ...metadata } = track.metadata
      const updatedTrack: Track = {
        ...track,
        metadata,
        metadata_extras: {
          ...track.metadata_extras,
          Lyrics: {
            ...track.metadata_extras?.Lyrics,
            ai_transcription_in_progress: true,
          },
        },
      }
      dispatch({
        type: TRACK_LOAD_BATCH,
        list: [updatedTrack],
      })
    }
  }
